(() => {
  const toneColors = {
    apple: '#d94335',
    mint: '#579b4b',
    pear: '#d0ad32',
    peach: '#e88b57',
    blackcurrant: '#5b3568'
  };

  const volumeConfigs = {
    '03l': { label: '0,3L', longLabel: 'sticlă de 0,3L', step: 24 },
    '1l': { label: '1L', longLabel: 'sticlă de 1L', step: 18 }
  };

  const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const initBuilder = (root) => {
    if (root.dataset.asbReady === 'true') return;
    root.dataset.asbReady = 'true';

    const sgrFee = Number(root.dataset.sgrFee || 50);
    const baxDiscountPercent = Number(root.dataset.baxDiscountPercent || 5);
    const currency = root.dataset.currency || 'RON';
    const storageKey = 'sucaroma-bax-mix-v2';
    const flow = root.querySelector('[data-asb-builder-flow]');
    const summary = root.querySelector('.asb-summary');
    const submit = root.querySelector('[data-asb-submit]');
    const nextBox = root.querySelector('[data-asb-next-box]');
    const status = root.querySelector('[data-asb-status]');
    const volumeStatus = root.querySelector('[data-asb-volume-status]');
    const targetButtons = Array.from(root.querySelectorAll('[data-asb-target-index]'));
    const addBox = root.querySelector('[data-asb-add-box]');

    const products = Array.from(root.querySelectorAll('[data-asb-product]')).map((card) => ({
      card,
      variantId: Number(card.dataset.variantId),
      title: card.dataset.productTitle,
      flavor: card.dataset.flavor,
      tone: card.dataset.tone,
      volume: card.dataset.volume,
      price: Number(card.dataset.price),
      available: card.dataset.available === 'true',
      quantity: 0,
      number: card.querySelector('.asb-flavor-card__number'),
      output: card.querySelector('[data-asb-quantity]'),
      minus: card.querySelector('[data-asb-minus]'),
      plus: card.querySelector('[data-asb-plus]')
    })).filter((product) => Number.isFinite(product.variantId) && volumeConfigs[product.volume]);

    let activeVolume = null;
    let isSubmitting = false;
    const pendingControls = new Map();
    const targets = { '03l': 24, '1l': 18 };

    const setSubmitting = (pending) => {
      isSubmitting = pending;
      if (pending) {
        root.querySelectorAll('button').forEach((button) => {
          pendingControls.set(button, button.disabled);
          button.disabled = true;
        });
      } else {
        pendingControls.forEach((disabled, button) => { button.disabled = disabled; });
        pendingControls.clear();
      }
    };

    const config = () => volumeConfigs[activeVolume] || volumeConfigs['03l'];
    const activeProducts = () => products.filter((product) => product.volume === activeVolume);
    const target = () => targets[activeVolume] || config().step;
    const totalCount = (volume = activeVolume) => products
      .filter((product) => product.volume === volume)
      .reduce((sum, product) => sum + product.quantity, 0);
    const productsTotal = () => activeProducts().reduce((sum, product) => sum + (product.quantity * product.price), 0);
    const productsDiscountTotal = () => activeProducts().reduce(
      (sum, product) => sum + Math.round((product.quantity * product.price * baxDiscountPercent) / 100),
      0
    );
    const isComplete = () => Boolean(activeVolume) && totalCount() === target() && target() % config().step === 0;

    const money = (cents) => new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(cents / 100);

    const announce = (message) => {
      if (status) status.textContent = message;
    };

    const bumpSummary = () => {
      summary?.classList.remove('is-bumping');
      window.requestAnimationFrame(() => summary?.classList.add('is-bumping'));
      window.setTimeout(() => summary?.classList.remove('is-bumping'), 320);
    };

    const save = () => {
      try {
        const quantities = Object.fromEntries(products.map((product) => [product.variantId, product.quantity]));
        localStorage.setItem(storageKey, JSON.stringify({ activeVolume, targets, quantities }));
      } catch (_error) {
        // Configuratorul funcționează și dacă stocarea locală este indisponibilă.
      }
    };

    const restore = () => {
      try {
        const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
        Object.keys(volumeConfigs).forEach((volume) => {
          const step = volumeConfigs[volume].step;
          const storedTarget = Number(stored.targets?.[volume]);
          if (storedTarget >= step && storedTarget % step === 0) targets[volume] = storedTarget;
        });
        products.forEach((product) => {
          const storedQuantity = Number(stored.quantities?.[product.variantId] || 0);
          product.quantity = product.available && storedQuantity > 0 ? Math.floor(storedQuantity) : 0;
        });
        Object.keys(volumeConfigs).forEach((volume) => {
          const count = totalCount(volume);
          const step = volumeConfigs[volume].step;
          if (count > targets[volume]) targets[volume] = Math.ceil(count / step) * step;
        });
        if (volumeConfigs[stored.activeVolume]) activeVolume = stored.activeVolume;
      } catch (_error) {
        activeVolume = null;
      }
    };

    const buildSequence = () => {
      const remaining = activeProducts().map((product) => ({ product, quantity: product.quantity }));
      const sequence = [];
      while (remaining.some((item) => item.quantity > 0)) {
        remaining.forEach((item) => {
          if (item.quantity > 0) {
            sequence.push(item.product);
            item.quantity -= 1;
          }
        });
      }
      return sequence;
    };

    const renderBoxes = () => {
      const boxesNode = root.querySelector('[data-asb-boxes]');
      if (!boxesNode || !activeVolume) return;

      const sequence = buildSequence();
      const step = config().step;
      const numberOfBoxes = Math.max(1, target() / step);
      const slotColumns = step === 18 ? 9 : 12;
      let html = '';

      for (let boxIndex = 0; boxIndex < numberOfBoxes; boxIndex += 1) {
        const boxStart = boxIndex * step;
        const boxFilled = Math.min(step, Math.max(0, sequence.length - boxStart));
        html += `<div class="asb-box"><div class="asb-box__heading"><span>Bax ${boxIndex + 1} · ${escapeHtml(config().label)}</span><strong>${boxFilled}/${step}</strong></div><div class="asb-box__slots" style="grid-template-columns:repeat(${slotColumns},1fr)">`;
        for (let slotIndex = 0; slotIndex < step; slotIndex += 1) {
          const product = sequence[boxStart + slotIndex];
          if (product) {
            const color = toneColors[product.tone] || toneColors.apple;
            html += `<span class="asb-slot is-filled" title="${escapeHtml(product.flavor)}" style="--slot-color:${color}"><i class="asb-slot__bottle"></i></span>`;
          } else {
            html += '<span class="asb-slot" aria-hidden="true"></span>';
          }
        }
        html += '</div></div>';
      }

      boxesNode.innerHTML = html;
    };

    const syncTargetControls = (count) => {
      if (!activeVolume) return;
      const step = config().step;
      targetButtons.forEach((button) => {
        const multiplier = Number(button.dataset.asbTargetIndex || 1);
        const buttonTarget = multiplier * step;
        button.dataset.asbTarget = buttonTarget;
        button.textContent = buttonTarget;
        button.setAttribute('aria-pressed', String(buttonTarget === target()));
        button.disabled = buttonTarget < count;
      });
      if (addBox) {
        addBox.textContent = `+${step}`;
        addBox.setAttribute('aria-label', `Adaugă încă ${step} de sticle`);
      }
      if (nextBox) nextBox.textContent = `Mai adaugă un bax de ${step}`;
    };

    const render = ({ persist = true } = {}) => {
      if (!activeVolume || isSubmitting) return;

      const count = totalCount();
      const currentTarget = target();
      const productCents = productsTotal();
      const discountCents = isComplete() ? productsDiscountTotal() : 0;
      const sgrCents = count * sgrFee;
      const percent = Math.min(100, Math.round((count / currentTarget) * 100));
      const remaining = Math.max(0, currentTarget - count);

      products.forEach((product) => {
        const isActive = product.volume === activeVolume;
        product.card.hidden = !isActive;
        if (product.output) product.output.textContent = product.quantity;
        product.card.classList.toggle('is-selected', isActive && product.quantity > 0);
        if (product.minus) product.minus.disabled = !isActive || !product.available || product.quantity === 0;
        if (product.plus) product.plus.disabled = !isActive || !product.available;
      });
      activeProducts().forEach((product, index) => {
        if (product.number) product.number.textContent = String(index + 1).padStart(2, '0');
      });

      root.querySelectorAll('[data-asb-total-count]').forEach((node) => { node.textContent = count; });
      root.querySelectorAll('[data-asb-target-count]').forEach((node) => { node.textContent = currentTarget; });
      root.querySelectorAll('[data-asb-percent]').forEach((node) => { node.textContent = `${percent}%`; });
      root.querySelectorAll('[data-asb-progress]').forEach((node) => { node.style.width = `${percent}%`; });
      root.querySelectorAll('[data-asb-ring]').forEach((node) => { node.style.setProperty('--progress', `${percent * 3.6}deg`); });
      root.querySelectorAll('[data-asb-products-total]').forEach((node) => { node.textContent = money(productCents); });
      root.querySelectorAll('[data-asb-discount-total]').forEach((node) => {
        node.textContent = discountCents > 0 ? `−${money(discountCents)}` : money(0);
      });
      root.querySelectorAll('[data-asb-discount-row]').forEach((node) => {
        node.classList.toggle('is-active', discountCents > 0);
      });
      root.querySelectorAll('[data-asb-sgr-total]').forEach((node) => { node.textContent = money(sgrCents); });
      root.querySelectorAll('[data-asb-grand-total]').forEach((node) => { node.textContent = money(productCents - discountCents + sgrCents); });
      root.querySelectorAll('[data-asb-selected-volume]').forEach((node) => { node.textContent = `${config().longLabel}, bax de ${config().step}`; });
      root.querySelectorAll('[data-asb-summary-format]').forEach((node) => { node.textContent = `${config().longLabel} · baxuri de ${config().step}`; });

      const message = root.querySelector('[data-asb-message]');
      if (message) {
        if (count === 0) message.textContent = `Mai alegi ${currentTarget} de sticle și primul bax de ${config().label} e gata.`;
        else if (remaining > 0) message.textContent = `Mai alegi ${remaining} ${remaining === 1 ? 'sticlă' : 'sticle'} și mixul este complet.`;
        else message.textContent = `${currentTarget} de sticle ${config().label} alese. Baxul este complet: plătești cu ${baxDiscountPercent}% mai puțin pentru sucuri.`;
      }

      syncTargetControls(count);
      if (submit) {
        submit.disabled = !isComplete() || activeProducts().length === 0;
        submit.querySelector('span').textContent = isComplete()
          ? `Pune baxul cu −${baxDiscountPercent}% în coș`
          : `Completează până la ${currentTarget}`;
      }
      if (nextBox) nextBox.hidden = !isComplete();

      renderBoxes();
      if (persist) save();
    };

    const chooseVolume = (volume, { scroll = true, persist = true } = {}) => {
      if (isSubmitting || !volumeConfigs[volume]) return;
      activeVolume = volume;
      root.dataset.asbVolume = volume;
      root.querySelectorAll('[data-asb-volume-choice]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.asbVolumeChoice === volume));
      });
      if (flow) flow.hidden = false;
      root.querySelectorAll('.asb-flow-progress span').forEach((node, index) => node.classList.toggle('is-active', index <= 1));
      if (volumeStatus) volumeStatus.textContent = `Ai ales ${config().longLabel}. Configuratorul a setat automat baxul de ${config().step}.`;
      announce(`Format selectat: ${config().longLabel}, bax de ${config().step}.`);
      render({ persist });
      if (scroll) flow?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const setTarget = (newTarget) => {
      if (!activeVolume || isSubmitting) return;
      const count = totalCount();
      const step = config().step;
      if (!Number.isFinite(newTarget) || newTarget < step || newTarget % step !== 0) return;
      if (newTarget < count) {
        announce(`Ai deja ${count} de sticle selectate. Redu cantitatea înainte să alegi un bax mai mic.`);
        bumpSummary();
        return;
      }
      targets[activeVolume] = newTarget;
      announce(`Ținta este acum ${newTarget} de sticle de ${config().label}.`);
      render();
    };

    root.querySelectorAll('[data-asb-volume-choice]').forEach((button) => {
      button.addEventListener('click', () => chooseVolume(button.dataset.asbVolumeChoice));
    });

    products.forEach((product) => {
      product.plus?.addEventListener('click', () => {
        if (isSubmitting || product.volume !== activeVolume) return;
        if (totalCount() >= target()) {
          announce(`Baxul de ${target()} este complet. Adaugă încă ${config().step} de locuri dacă vrei să continui.`);
          bumpSummary();
          return;
        }
        product.quantity += 1;
        announce(`${product.flavor}: ${product.quantity} ${product.quantity === 1 ? 'sticlă' : 'sticle'} de ${config().label}.`);
        render();
      });

      product.minus?.addEventListener('click', () => {
        if (isSubmitting || product.volume !== activeVolume) return;
        product.quantity = Math.max(0, product.quantity - 1);
        announce(`${product.flavor}: ${product.quantity} ${product.quantity === 1 ? 'sticlă' : 'sticle'} de ${config().label}.`);
        render();
      });
    });

    targetButtons.forEach((button) => {
      button.addEventListener('click', () => setTarget(Number(button.dataset.asbTarget)));
    });
    addBox?.addEventListener('click', () => setTarget(target() + config().step));
    nextBox?.addEventListener('click', () => setTarget(target() + config().step));

    root.querySelectorAll('[data-asb-recipe]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!activeVolume || isSubmitting) return;
        const recipe = button.dataset.asbRecipe;
        const availableProducts = activeProducts().filter((product) => product.available);
        if (!availableProducts.length) return;
        activeProducts().forEach((product) => { product.quantity = 0; });

        if (recipe === 'balanced') {
          const base = Math.floor(target() / availableProducts.length);
          let remainder = target() % availableProducts.length;
          availableProducts.forEach((product) => {
            product.quantity = base + (remainder > 0 ? 1 : 0);
            remainder = Math.max(0, remainder - 1);
          });
          announce('Am împărțit baxul cât mai egal între sortimentele disponibile. Îl poți ajusta cum vrei.');
        } else if (recipe === 'apple') {
          const apple = availableProducts.find((product) => product.tone === 'apple') || availableProducts[0];
          apple.quantity = target();
          announce(`Baxul este plin cu suc de mere la ${config().label}.`);
        } else if (recipe === 'surprise') {
          for (let index = 0; index < target(); index += 1) {
            availableProducts[Math.floor(Math.random() * availableProducts.length)].quantity += 1;
          }
          announce('Am pregătit un mix-surpriză. Îl poți regla înainte de comandă.');
        } else {
          announce('Baxul este gol. Poți începe un mix nou.');
        }
        render();
      });
    });

    submit?.addEventListener('click', async () => {
      if (isSubmitting || !isComplete()) return;
      const selected = activeProducts().filter((product) => product.quantity > 0);
      if (!selected.length) return;

      const currentTarget = target();
      const mixDescription = selected.map((product) => `${product.quantity} × ${product.flavor}`).join(', ');
      const groupId = `AROMA-${activeVolume.toUpperCase()}-${Date.now()}`;
      const items = selected.map((product) => ({
        id: product.variantId,
        quantity: product.quantity,
        properties: {
          'Format sticlă': config().label,
          'Bax personalizat': `${currentTarget} sticle`,
          'Aromă în mix': `${product.quantity} × ${product.flavor}`,
          '_Mix complet': mixDescription,
          '_Grup configurator': groupId
        }
      }));

      setSubmitting(true);
      submit.classList.add('is-loading');
      submit.disabled = true;
      submit.querySelector('span').textContent = 'Pregătim baxul…';
      announce('Adăugăm mixul în coș.');

      try {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Mixul nu a putut fi adăugat. Verifică stocul și încearcă din nou.');
        }

        try {
          localStorage.removeItem(storageKey);
        } catch (_error) {
          // Coșul este deja confirmat; curățarea stocării nu trebuie să invite o nouă adăugare.
        }
        announce('Baxul este în coș. Te ducem la comandă…');
        submit.querySelector('span').textContent = 'Bax adăugat în coș';
        window.setTimeout(() => { window.location.href = `${rootUrl}cart`; }, 650);
      } catch (error) {
        setSubmitting(false);
        submit.classList.remove('is-loading');
        render({ persist: false });
        announce(error.message || 'A apărut o problemă. Încearcă din nou.');
      }
    });

    const requestedVolume = new URLSearchParams(window.location.search).get('format')
      || window.location.hash.replace('#format-', '');
    const isDirectVolumeLink = window.location.hash.startsWith('#format-') && Boolean(volumeConfigs[requestedVolume]);
    restore();
    if (volumeConfigs[requestedVolume]) {
      chooseVolume(requestedVolume, { scroll: false, persist: true });
      if (isDirectVolumeLink) window.requestAnimationFrame(() => flow?.scrollIntoView({ block: 'start' }));
    }
    else if (activeVolume) chooseVolume(activeVolume, { scroll: false, persist: false });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-asb-builder]').forEach(initBuilder);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
